import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CurrencyInr, ChartBar, ArrowSquareOut, Bank, Receipt, TrendUp, Warning } from "@phosphor-icons/react";

const fmtINR = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

function Tile({ label, value, sub, icon: Icon, tone = "primary", testid }) {
  const tones = {
    primary: "border-[var(--brand-primary,#263776)] text-[var(--brand-primary,#263776)]",
    success: "border-[var(--status-success,#16a34a)] text-[var(--status-success,#16a34a)]",
    warning: "border-[var(--status-warning,#d97706)] text-[var(--status-warning,#d97706)]",
    danger:  "border-[var(--status-danger,#dc2626)] text-[var(--status-danger,#dc2626)]",
    info:    "border-[var(--status-info,#2563eb)] text-[var(--status-info,#2563eb)]",
  };
  return (
    <div data-testid={testid} className={`bg-[var(--surface,#fff)] border-l-4 ${tones[tone]} rounded shadow-sm p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">{label}</div>
        {Icon && <Icon size={18} weight="duotone" className="opacity-70" />}
      </div>
      <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
      {sub && <div className="text-[11px] text-[var(--text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

export default function FinanceDashboard() {
  const [finance, setFinance] = useState(null);
  const [tax, setTax] = useState(null);
  const [aging, setAging] = useState(null);
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, b, c, d] = await Promise.all([
          api.get(`/analytics/finance?year=${year}`).catch(() => ({ data: null })),
          api.get(`/finance/tax-dashboard?year=${year}`).catch(() => ({ data: null })),
          api.get(`/analytics/receivables-aging`).catch(() => ({ data: null })),
          api.get(`/finance/saturday-payout-drill`).catch(() => ({ data: null })),
        ]);
        setFinance(a.data); setTax(b.data); setAging(c.data); setPayout(d.data);
      } catch (e) { setError(e.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, [year]);

  const downloadExport = async (resource, fmt) => {
    try {
      const { data } = await api.get(`/reports/${resource}.${fmt}`, { responseType: "blob" });
      const blob = new Blob([data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `span_${resource}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alert("Export failed: " + (e?.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="p-8 text-[var(--text-muted)]">Loading Finance dashboard…</div>;
  if (error)   return <div className="p-8 text-[var(--status-danger)]">Error: {error}</div>;

  const totalRevenue = finance?.total_inr || tax?.revenue_inr || 0;
  const gstAmount    = finance?.gst_18_inr || tax?.gst_inr || 0;
  const tdsAmount    = finance?.tds_194j_inr || tax?.tds_inr || 0;
  const outstanding  = aging?.total_outstanding || 0;
  const unpaidCount  = aging?.unpaid_count || 0;
  const oldest       = aging?.oldest_days || 0;
  const buckets      = aging?.buckets || {};

  return (
    <div className="p-6 space-y-6" data-testid="finance-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Finance</div>
          <h1 className="font-display font-bold text-3xl mt-1 text-[var(--text-primary)]">Finance Dashboard · {year}</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">Live revenue · GST · TDS · receivables aging · payouts.</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { r: "invoices", label: "Invoices" },
            { r: "receivables", label: "Receivables" },
            { r: "expenses", label: "Expenses" },
          ].map(x => (
            <div key={x.r} className="flex items-center gap-1 bg-[var(--surface,#fff)] border border-[var(--border)] rounded">
              <span className="text-[11px] px-2 text-[var(--text-secondary)]">{x.label}</span>
              {["csv","xlsx","pdf","docx"].map(f => (
                <button
                  key={f}
                  onClick={() => downloadExport(x.r, f)}
                  data-testid={`fin-export-${x.r}-${f}`}
                  className="text-[10px] uppercase px-1.5 py-1 hover:bg-[var(--surface-2)] font-mono">{f}</button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile testid="tile-revenue"     label="Revenue YTD"      value={fmtINR(totalRevenue)}  sub={`${finance?.invoice_count || 0} invoices`}   icon={CurrencyInr} tone="success" />
        <Tile testid="tile-gst"         label="GST 18% collected" value={fmtINR(gstAmount)}    sub="To be remitted to GSTN"                       icon={Receipt}     tone="primary" />
        <Tile testid="tile-tds"         label="TDS 194J estimate" value={fmtINR(tdsAmount)}    sub="Client-withholding estimate"                  icon={Bank}        tone="info" />
        <Tile testid="tile-outstanding" label="Receivables outstanding" value={fmtINR(outstanding)} sub={`${unpaidCount} unpaid · oldest ${oldest}d`} icon={Warning}     tone={outstanding > 0 ? "warning" : "neutral"} />
      </div>

      {/* Aging buckets */}
      <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-[var(--text-primary)]">Receivables Aging</h2>
          <Link to="/receivables" className="text-[11px] text-[var(--brand-primary,#263776)] flex items-center gap-1 hover:underline" data-testid="aging-link">
            View ledger <ArrowSquareOut size={11} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["0-30 days",   buckets["0-30"],   "success"],
            ["31-60 days",  buckets["31-60"],  "info"],
            ["61-90 days",  buckets["61-90"],  "warning"],
            ["90+ days",    buckets["90+"],    "danger"],
          ].map(([lbl, bucket, tone]) => (
            <div key={lbl} className={`border-l-4 border-[var(--status-${tone})] bg-[var(--surface-2)] rounded p-3`} data-testid={`aging-bucket-${lbl}`}>
              <div className="text-[10px] uppercase text-[var(--text-muted)] font-semibold">{lbl}</div>
              <div className="text-lg font-bold text-[var(--text-primary)] mt-1">{fmtINR(bucket?.total || 0)}</div>
              <div className="text-[10px] text-[var(--text-muted)]">{bucket?.count || 0} invoice(s)</div>
            </div>
          ))}
        </div>
      </div>

      {/* By-status + Saturday payout side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5">
          <h2 className="font-bold text-[var(--text-primary)] mb-3 flex items-center gap-2"><ChartBar size={16} /> Invoices by status</h2>
          {finance?.by_status ? (
            <table className="w-full text-sm" data-testid="status-table">
              <thead className="text-[10px] uppercase text-[var(--text-muted)]">
                <tr><th className="text-left py-1">Status</th><th className="text-right py-1">Count</th><th className="text-right py-1">Total (INR)</th></tr>
              </thead>
              <tbody>
                {Object.entries(finance.by_status).map(([s, v]) => (
                  <tr key={s} className="border-t border-[var(--border)]">
                    <td className="py-1.5 capitalize">{s}</td>
                    <td className="text-right font-mono">{v.count || 0}</td>
                    <td className="text-right font-mono">{fmtINR(v.total_inr || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="text-[12px] text-[var(--text-muted)]">No data.</div>}
        </div>

        <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5">
          <h2 className="font-bold text-[var(--text-primary)] mb-3 flex items-center gap-2"><TrendUp size={16} /> Saturday payout drill</h2>
          {payout ? (
            <div className="space-y-2 text-sm" data-testid="payout-block">
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">Next Saturday</span><span className="font-mono font-semibold">{payout.next_saturday || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">Contractor invoices</span><span className="font-mono">{payout.contractor_count || 0}</span></div>
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">Expense claims</span><span className="font-mono">{payout.expense_count || 0}</span></div>
              <div className="flex justify-between text-base pt-2 border-t border-[var(--border)] mt-2"><span className="font-semibold">Total payout</span><span className="font-mono font-bold text-[var(--brand-primary)]">{fmtINR(payout.total_inr || 0)}</span></div>
            </div>
          ) : <div className="text-[12px] text-[var(--text-muted)]">No payout drill data.</div>}
          <Link to="/payables" className="text-[11px] text-[var(--brand-primary,#263776)] flex items-center gap-1 hover:underline mt-3" data-testid="payables-link">
            Open payables <ArrowSquareOut size={11} />
          </Link>
        </div>
      </div>

      {/* Quick links */}
      <div className="flex flex-wrap gap-2 pt-2">
        <Link to="/invoices" className="span-btn span-btn-ghost" data-testid="link-invoices">Invoices</Link>
        <Link to="/receivables" className="span-btn span-btn-ghost" data-testid="link-receivables">Receivables</Link>
        <Link to="/payables" className="span-btn span-btn-ghost" data-testid="link-payables">Payables</Link>
        <Link to="/analytics" className="span-btn span-btn-ghost" data-testid="link-analytics">Full analytics</Link>
      </div>
    </div>
  );
}
